'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useEnvContext } from 'next-runtime-env'

import { faLock, faUser } from '@document-crunch/pro-regular-svg-icons'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'
import { getTrimbleAuthManager } from '@/integrations/trimble/trimbleApi'

import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@dc-shared-web/components/shadcn/sheet'
import { Button } from '@dc-shared-web/components/shadcn/button'

import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { Avatar } from '@/dc-shared-web/src'

export default function ProfileSidePane() {
  const { NEXT_PUBLIC_DCRUNCH_API_URL, NEXT_PUBLIC_PROJECTSIGHT_AUTH_URL } =
    useEnvContext()
  const linkPrefix = NEXT_PUBLIC_DCRUNCH_API_URL
  const { user } = useAuth()
  const [open, setOpen] = useState(false)

  const ssoEnabled = useMemo(() => {
    return user?.teams?.some((team) => team.entitlements?.use_sso)
  }, [user])

  const handleLogout = async () => {
    try {
      // Logout from Trimble first
      const authManager = getTrimbleAuthManager(
        NEXT_PUBLIC_PROJECTSIGHT_AUTH_URL || ''
      )
      await authManager.logout()
    } catch (error) {
      console.error('Trimble logout error:', error)
    } finally {
      // Redirect to the main application's logout URL
      window.location.href = `${linkPrefix}/logout`
    }
  }

  const initials = useMemo(() => {
    if (!user?.name) {
      return ''
    }
    const firstName = user.name.split(' ')?.[0]
    const lastName = user.name.split(' ')?.[1]
    return `${firstName?.[0]}${lastName?.[0]}`
  }, [user])

  return (
    <Sheet open={open} onOpenChange={() => setOpen(!open)}>
      <SheetTrigger asChild>
        <button data-testid='userpane-open-btn'>
          <Avatar fallback={initials} size='sm' fallbackColor='blue' />
        </button>
      </SheetTrigger>
      <SheetContent
        showOverlay={false}
        className='tw-w-1/4 tw-text-neutral-700 tw-z-[100]'
      >
        <SheetTitle>Profile</SheetTitle>
        <div className='tw-font-medium tw-pt-3' data-testid='userpane-name'>
          {user?.name}
        </div>
        <div
          className='tw-text-muted-foreground tw-text-sm'
          data-testid='userpane-email'
        >
          {user?.email}
        </div>
        <Button
          className='tw-mt-2'
          onClick={handleLogout}
          data-testid='userpane-logout-btn'
        >
          Sign Out
        </Button>
        <hr className='tw-mt-5' />

        <Link
          href={`${linkPrefix}/user`}
          className='tw-flex tw-items-center tw-mt-7 tw-space-x-3 hover:tw-cursor-pointer group'
          data-testid='userpane-profile-link'
        >
          <Button variant='gray' size='icon'>
            <Icon icon={faUser} color={IconColor.muted} />
          </Button>
          <div>
            <div className='tw-text-sm tw-font-medium tw-text-neutral-700'>
              My Profile
            </div>
            <div className='tw-text-sm tw-text-muted-foreground'>
              Account settings and more
            </div>
          </div>
        </Link>

        {!ssoEnabled && (
          <Link
            href={`${linkPrefix}/change-password`}
            className='tw-flex tw-items-center tw-mt-5 tw-mb-10 tw-space-x-3 hover:tw-cursor-pointer group'
            onClick={() => setOpen(false)}
            data-testid='userpane-change-password-link'
          >
            <Button variant='gray' size='icon'>
              <Icon icon={faLock} color={IconColor.muted} />
            </Button>
            <div>
              <div className='tw-text-sm tw-font-medium tw-text-neutral-700'>
                Password
              </div>

              <div className='tw-text-sm tw-text-muted-foreground'>
                Change password
              </div>
            </div>
          </Link>
        )}
      </SheetContent>
    </Sheet>
  )
}
